import { useModal } from '@billy/design-system'
import React, { ReactElement, useCallback, useId, useState } from 'react'
import { useQueryClient } from 'react-query'

import { EmberSaveAccountProps } from '@features/chartOfAccounts/types/emberSaveAccountProps'
import { AccountGroupsContextProvider } from '@modules-deprecated/app/accounts'
import { Account, AccountNature } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { CreateAccountFromTemplateModal } from '@views/settings/routes/OrganizationChartOfAccounts/elements/AccountFromTemplateModal/elements/CreateAccountFromTemplateModal'
import { EditAccountModal } from '@views/settings/routes/OrganizationChartOfAccounts/elements/EditAccountModal'

import { ModalId } from '../../../enums/modalId'
import { QueryKeys } from '../../../enums/queryKeys'
import { shouldUseDKFeatures } from '../../../utils/shouldUseDKFeatures'

interface UseCreateAccountModalProps {
  natures?: AccountNature[]
  onSave?: (account: Account) => void
}

interface UseCreateAccountModalResult {
  openCreateAccountModal: () => void
  createAccountModal: ReactElement | null
}

export const useCreateAccountModal = ({
  natures,
  onSave,
}: UseCreateAccountModalProps = {}): UseCreateAccountModalResult => {
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()
  const [isLegacyModalOpen, setIsLegacyModalOpen] = useState(false)
  // Modal ids are global, so each instance needs its own id to not open another instance's modal
  const templateModalId = `${ModalId.AccountFromTemplateModalCreate}-${useId()}`
  const { open: openTemplateModal, close: closeTemplateModal } = useModal(templateModalId)

  const shouldCreateAccountFromTemplate = !!organization?.countryId && !shouldUseDKFeatures(organization.countryId)

  const openCreateAccountModal = useCallback(() => {
    if (shouldCreateAccountFromTemplate) {
      openTemplateModal()
    } else {
      setIsLegacyModalOpen(true)
    }
  }, [openTemplateModal, shouldCreateAccountFromTemplate])

  const handleAccountCreated = useCallback(
    async (account: Account) => {
      await queryClient.invalidateQueries(QueryKeys.Accounts)
      onSave?.(account)
    },
    [onSave, queryClient],
  )

  const handleLegacyModalClose = useCallback(() => {
    setIsLegacyModalOpen(false)
  }, [])

  const handleLegacyModalSave = useCallback(
    (response?: EmberSaveAccountProps) => {
      if (response?.account) {
        handleAccountCreated(response.account)
      }
    },
    [handleAccountCreated],
  )

  const templateModal = (
    <CreateAccountFromTemplateModal
      id={templateModalId}
      natures={natures}
      onClose={closeTemplateModal}
      onSave={handleAccountCreated}
    />
  )

  const legacyModal = isLegacyModalOpen ? (
    <AccountGroupsContextProvider organizationId={organization?.id}>
      <EditAccountModal isOpen onClose={handleLegacyModalClose} onSave={handleLegacyModalSave} />
    </AccountGroupsContextProvider>
  ) : null

  const createAccountModal = shouldCreateAccountFromTemplate ? templateModal : legacyModal

  return { openCreateAccountModal, createAccountModal }
}
